import { Injectable } from '@angular/core';

import { Profile, ProfileTier } from '@fcom/core-api/login';
import { LocalDate } from '@fcom/core/utils';

import {
  AVIOS_LIFETIME_TIER_LIMITS,
  AVIOS_TIER_LIMITS,
  LIFETIME_TIER_LIMITS,
  LIFETIME_TIER_TEXT,
  TIER_DATA_MAP,
  TIER_FLIGHT_LIMITS,
  TIER_LIMITS,
  TierName,
  TierState,
} from '../../interfaces';

@Injectable()
export class TierLimitService {
  public tierExpiryDate(memberProfile: Profile, avios: boolean): LocalDate {
    return new LocalDate(memberProfile.currentPeriod.trackingPeriodEnd).plusYears(
      this.getTierState(memberProfile, avios) === TierState.GAIN ? 1 : 0
    );
  }

  public getTierState(memberProfile: Profile, avios: boolean): TierState {
    const tierLimits = avios ? AVIOS_TIER_LIMITS : TIER_LIMITS;

    if (memberProfile.statusHold) {
      return TierState.HOLD;
    }
    // Basic tier user can only gain points to Silver
    if (memberProfile.tier === ProfileTier.BASIC) {
      return TierState.GAIN;
      // If the user has gained less points in the current period than is needed for the current tier, show "maintain tier" state
    } else if (
      tierLimits[memberProfile.tier] > memberProfile.currentPeriod.tierPointsCollected &&
      TIER_FLIGHT_LIMITS[memberProfile.tier] > memberProfile.currentPeriod.qualifyingFlightsFlown &&
      !this.lifetimeTier(memberProfile, avios)
    ) {
      return TierState.MAINTAIN;
      // If the user has gained more points than needed for the current tier, but the current tier is lumo, show "you have reached highest tier" text
    } else if (memberProfile.tier === ProfileTier.LUMO) {
      if (
        memberProfile.currentPeriod.aySecretNumQFlightCalc < memberProfile.currentPeriod.aySecretNumQFlightRequired &&
        memberProfile.currentPeriod.aySecretTierPoints < memberProfile.currentPeriod.aySecretTierPointsRequired
      ) {
        return TierState.MAINTAIN;
      }
      return TierState.HIGHEST;
      // If the user has gained more points than needed for the current tier, which is not Basic or Lumo, show "gain next tier" state
    } else {
      return TierState.GAIN;
    }
  }

  public lifetimeTier(memberProfile: Profile, avios: boolean): ProfileTier {
    const lifetimeTierLimits = avios ? AVIOS_LIFETIME_TIER_LIMITS : LIFETIME_TIER_LIMITS;

    if (memberProfile.lifetimeTierPoints >= lifetimeTierLimits[ProfileTier.PLATINUM]) {
      return ProfileTier.PLATINUM;
    } else if (memberProfile.lifetimeTierPoints >= lifetimeTierLimits[ProfileTier.GOLD]) {
      return ProfileTier.GOLD;
    }
    return undefined;
  }

  public getCurrentTierText(memberProfile: Profile, avios: boolean): string {
    const currentLifetimeTier: ProfileTier | undefined = memberProfile.isJuniorMember
      ? undefined
      : this.lifetimeTier(memberProfile, avios);

    const isCurrentTierTheLifetimeTier =
      (currentLifetimeTier === ProfileTier.PLATINUM && memberProfile.tier !== ProfileTier.LUMO) ||
      (currentLifetimeTier === ProfileTier.GOLD &&
        memberProfile.tier !== ProfileTier.LUMO &&
        memberProfile.tier !== ProfileTier.PLATINUM);

    const currentTier = isCurrentTierTheLifetimeTier
      ? `${LIFETIME_TIER_TEXT} ${currentLifetimeTier}`
      : TIER_DATA_MAP[memberProfile.tier]?.tierName ?? '';

    if (memberProfile.isJuniorMember && memberProfile.tier !== ProfileTier.JUNIOR) {
      return `${TierName.JUNIOR} ${currentTier}`;
    }

    return currentTier;
  }
}
